import BaseService from '../../base/base_service';
import _ from '../../utils/underscore';
import Event from '../../utils/event';
import DOM from '../../utils/dom';
import { SYSTEM_EVENTS } from '../../events/events';
import Directive from '../../core/directive';
import { FP_MESSAGE_MODE } from '../../../../common/const/free_play_consts';
import { PRIORITY_LEVEL } from '../overlay_manager/overlay_manager_levels';
import { FreePlayVoucherList } from './free_play_voucher_list';
import { FreePlayVoucher} from './Free_play_voucher';
import Store from '../../utils/store';
import IFreePlayService from './free_play_service_interface';
import { GAMES_INFO } from '../games_information/games_information_service_interface';
import { IAlteredFreePlayVoucher } from '../../components/free_play/voucher/free_play_voucher_consts';
import {
    IFreePlayVoucherData,
    IFreePlayVoucherDelta
} from "../../../../common/interfaces/free-play/free-play-voucher-data.interface";

const FREE_PLAY_LOCAL_STORAGE_KEY = "fp_";

export default class FreePlayService extends BaseService implements IFreePlayService {
    /**
     * @static
     * @type {string}
     * @type {string}
     */
    static CLASS = 'FreePlayService';

    private _messageMode = '';

    private _shouldAutoOpenMessage = false;

    private _tripleEightVouchers = new FreePlayVoucherList();

    private _oAuthResolver = null;

    /**
     *
     * @param params
     */
    init(params) {
        super.init(params);
        this._messageMode = params.messageMode || FP_MESSAGE_MODE.POPUP;
        this._shouldAutoOpenMessage = _.toBoolean(params.isOpenMesage);
        this._subscribe();
    }

    private _subscribe() {
        this.onSystemEvent(SYSTEM_EVENTS.SESSION_EVENT_FREE_PLAY_RECEIVED, (data: IFreePlayVoucherData[]) => {
            this._onFreePlayReceivedHandler(data);
            this._notifyOnFreePlayChanges();
        });

        this.onSystemEvent(SYSTEM_EVENTS.SESSION_EVENT_FREE_PLAY_REMOVED, (id: string) => {
            this._onFreePlayRemoved(id);
            this._notifyOnFreePlayChanges();
        });

        this.onSystemEvent(SYSTEM_EVENTS.OAUTH_STATE_CHANGE, () => {
            this._onAuthChanged()
                .then(() => {
                    if (this._shouldAutoOpenMessage) {
                        this._openVouchersMessage({});
                        this._oAuthResolver = null;
                    }
                })
                .catch(() => { // not authorized, notify about removing all vouchers
                    this._notifyOnFreePlayChanges();
                    this._oAuthResolver = null;
                });
        });

        this.onSystemEvent(SYSTEM_EVENTS.FREE_PLAY_OPEN_VOUCHERS_MSG, (opts) => {
            this._openVouchersMessage(opts);
        });

        this.onSystemEvent(SYSTEM_EVENTS.FREE_PLAY_VOUCHER_MSG_OPENED, () => {
            this._onVoucherMessageDisplayed();
            this._notifyOnFreePlayChanges();
        });

        this.onSystemEvent(SYSTEM_EVENTS.SESSION_EVENT_THIRD_PARTY_FREE_PLAY_UPDATED, () => {
            this._notifyOnFreePlayChanges();
        });
    }

    // gets all vouchers (including third party ones stored in thirdPartyService)
    // vouchers are returned sorted by expiration date
    private getAllVouchers(): FreePlayVoucherList {
        const thirdPartyVouchers = this.serviceFactory.thirdPartyFreePlayService().getStoredFreePlays();
        if (!_.isArray(thirdPartyVouchers) || thirdPartyVouchers.length === 0) {
            return new FreePlayVoucherList(this._tripleEightVouchers.values());
        }
        const mergedVouchersStore = this._tripleEightVouchers.appendVouchers(thirdPartyVouchers);
        return mergedVouchersStore;
    }


    /**
     * Save free play ids in local storage for specified user
     * @param cid
     * @param ids
     */
    private _saveDisplayedFreePlay(cid: string, ids: string[]) {
        if (_.isUndefined(ids) || _.isNull(ids)) {
            this.logger.warn('Unable to save free play ids. Data is null or empty');
            return;
        }

        this._persistModel(cid, ids);
    }

    /**
     * Save model state in local storage
     * @private
     */
    private _persistModel(cid: string, data: any) {
        Store.set(FREE_PLAY_LOCAL_STORAGE_KEY + cid, JSON.stringify(data));
    }

    /**
     * Return freeplay ids that were displayed previously from local storage to specified user (cid)
     * @param cid
     * @returns {undefined|*}
     */
    getDisplayedFreePlayIDs(cid: string): string[] {
        return _.safeJSON(Store.get(FREE_PLAY_LOCAL_STORAGE_KEY + cid)) as string[];
    }

    /*
    private sendAnalytics(label, value) {
        const analyticObj  =  {
            category: '[Technical-event]',
            action: 'freeplay',
            label: label};
        Event.fire(SYSTEM_EVENTS.ANALYTIC_TRIGGER_ACTIVATED, analyticObj);
    }
    */

    /**
     * When message opened, all vouchers ids should be updated as not new
     */
    private _onVoucherMessageDisplayed() {
        const displayedIDs = this.getAllVouchers().values().map(voucher => voucher.ID);
        this._saveDisplayedFreePlay(this.serviceFactory.userService().getCID(), displayedIDs);
    }

    private _onFreePlayReceivedHandler(freePlayData: IFreePlayVoucherDelta[]) {
        if (!freePlayData || !_.isArray(freePlayData)) {
            this.logger.warn(`_onFreePlayReceivedHandler received invalid content [${JSON.stringify(freePlayData)}]`);
            return;
        }
        freePlayData.forEach(voucherDelta => {
            let voucherToCheck: IFreePlayVoucherData = { ...voucherDelta } as IFreePlayVoucherData;

            // firebase sends only updated fields here
            // so we need a way to merge voucher with old data
            const before = this._tripleEightVouchers.getByID(voucherDelta.ID);
            if (before) {
                voucherToCheck = FreePlayVoucherList.getMergedResult(before, voucherDelta);
            }

            // for now, we don't check expiration date
            if (!FreePlayVoucher.isValid(voucherToCheck, false)) {
                this.logger.warn(`Received voucher ${JSON.stringify(voucherToCheck)} is invalid`);
                return;
            }
            this._onFreePlayReceived(voucherToCheck);
        });
    }

    private _onFreePlayReceived(voucher: IFreePlayVoucherData) {
        const isFirstVoucher = this._tripleEightVouchers.length === 0;
        const result = this._tripleEightVouchers.addOrUpdate(voucher);
        // voucher already exists, so we need to update it only
        if (!result.wasAdded) {
            this.logger.debug(`Voucher with ID ${voucher.ID} already exists. Voucher delta${JSON.stringify(voucher)}`);
            this.logger.debug(`Voucher ${voucher.ID} was merged. New voucher is: `, result.updatedValue);
        } else {
            this.logger.debug(`Voucher [${JSON.stringify(voucher)}] was added to voucher list`);
        }
        this.logger.debug('Vouchers model has been updated. New model is: ', this._tripleEightVouchers.values());
        // after receiving the first voucher we delay openVoucherMessage in order to waiting for another vouchers
        if (isFirstVoucher && this._oAuthResolver) {
            DOM.setTimeout(() => {
                this.logger.debug('End free play aggregation');
                this._oAuthResolver.resolve();
            }, 500);
        }
    }

    /**
     * Remove FreePlay
     * @param id
     */
    private _onFreePlayRemoved(id: string) {
        if (!id) {
            return;
        }
        if (!this._tripleEightVouchers.length) {
            return;
        }
        const removedVoucher = this._tripleEightVouchers.remove(id);
        if (removedVoucher) {
            this.logger.debug(`Voucher with id =${removedVoucher.ID} was removed.`);
            Event.fire(SYSTEM_EVENTS.FREE_PLAY_VOUCHER_REMOVED, removedVoucher);
        } else {
            this.logger.warn(`Voucher with id =${id} was not found. Unable to remove voucher`);
        }
    }


    /**
     * Open voucher message or fire an Event to B2C to open it in case of embedded mode
     * @param selectedVoucher - voucher that should be opened automatically after opening the message
     * @param gameTypeID - gametypeid for filtering vouchers
     * @private
     */
    private _openVouchersMessage(opts: any) {
        return this._getVouchersMessageParams(opts)
            .then((params) => {
                this._openVouchersMessageByMode(params);
            })
            .catch(() => {
                this.logger.debug('There is no vouchers for the user or vouchers filter process has been finished unexpectedly');
            });
    }


    /**
     * Open vouchers message according to mode.
     * In embedded it fires an event to B2C
     * In popup it creates and shows FreePlayComponent
     * @param messageProps
     * @private
     */
    private _openVouchersMessageByMode(messageProps) {
        if (!messageProps || !messageProps.vouchers) {
            this.throwError('240', 'messageProps parameter is missing or is not contain vouchers. Unable to open vouchers message', this);
            return;
        }
        const mode = messageProps.mode || this._messageMode;
        if (mode === FP_MESSAGE_MODE.EMBEDDED_INTERNAL) {
            Event.fire(SYSTEM_EVENTS.FREE_PLAY_OPEN_INTERNAL_VOUCHERS_MESSAGE, messageProps);
            return;
        } else {
            const vouchersMessage = Directive.generate({ directiveName: 'free_play', props: messageProps });
            // vouchersMessage.showMessage();
            vouchersMessage.start();
            Event.fire(SYSTEM_EVENTS.OPEN_OVERLAY, {
                component: vouchersMessage,
                priority: messageProps.openPriority || PRIORITY_LEVEL.FP,
                isCloseBtnAllowed: true,
                isHasHeader: false
            });
        }
    }


    /**
     * Prepare parameters to open vouchers message
     * If there are no vouchers, then returns rejected Promise
     * @param selectedVoucher
     * @param gameTypeID
     * @returns {Promise<T>} where T is the FreePlayComponent parameters
     * @private
     */
    private _getVouchersMessageParams(opts: any) {
        return this._getFreePlayDataForUser(opts && opts.filter).then((vouchers) => {
            if (!_.isArray(vouchers) || 0 === vouchers.length) {
                this.logger.warn('Vouchers message can not be shown because there are no vouchers exist');
                return Promise.reject('No vouchers');
            }
            // fix selected voucher format. It should be string only and not an object
            // const selectedVoucher = _.isString(opts.selectedVoucher) ? selectedVoucher : null;
            return Promise.resolve({
                vouchers: vouchers,
                voucher: opts.selectedVoucher,
                gameTypeID: opts.filter ? opts.filter.gameTypeID : null,
                mode: opts.mode,
                openPriority: opts.openPriority,
                direction: opts.openDirection
            });
        });
    }

    /**
     * Returns free play data for current user and filter vouchers according to available games
     * @returns {*}
     * @private
     */
    private _getFreePlayDataForUser(filter?: { gameTypeID: number, excludeFreePlays?: string[] }): Promise<IFreePlayVoucherData[]> {
        if (!this.serviceFactory.oAuth2Service().isAuthorized()) {
            return Promise.resolve([]);
        }

        const userCID = this.serviceFactory.userService().getCID();
        return this.serviceFactory.integrationService().getAllGameTypes()
            .then((gameTypes: number[]) => {
                const filterGameTypes =  filter && filter.gameTypeID ? [filter.gameTypeID] : gameTypes;
                return this.serviceFactory.gamesInformationService().getGamesInfo(filterGameTypes);
            })
            .then((availableGames: GAMES_INFO) => {

                // filter vouchers
                let vouchers = this._filterVouchers(userCID, availableGames);
                if (_.isArray(vouchers) && vouchers.length > 0) {
                    this.logger.debug(`Found ${vouchers.length} vouchers`);
                    this.logger.debug(`Vouchers: ${JSON.stringify(vouchers.map(v => v.ID))}`);
                } else {
                    this.logger.debug(`There are no vouchers found for user ${userCID}`);
                }

                // exclude vouchers
                if (filter && filter.excludeFreePlays) {
                    vouchers = this._excludeVouchers(vouchers, filter.excludeFreePlays);
                }

                return this._sortByGameRecommendations(vouchers);
            });
    }

    // sort games in each voucher
    private _sortByGameRecommendations(vouchers: IFreePlayVoucherData[]): Promise<IFreePlayVoucherData[]> {
        const arrPromises: Array<Promise<any>> = [];

        vouchers.forEach(voucher => {
            const voucherPromise = this.serviceFactory.aiListService().sortGames(voucher.GameTypeIDList)
            .then(sortedGamesList => {
                voucher.GameTypeIDList = sortedGamesList;
                return sortedGamesList;
            }).catch(err => {
                // swallow error
                this.logger.warn(`Failed to sort games for voucher ${voucher.ID}`, err);
                return voucher.GameTypeIDList;
            });

            arrPromises.push(voucherPromise);
        });

        return Promise.all(arrPromises).then(() => vouchers);
    }

    /**
     * Exclude vouchers
     * @param vouchers
     * @param excludeVouchersList
     * @returns {*}
     * @private
     */
    private _excludeVouchers(vouchers: IAlteredFreePlayVoucher[], excludeVouchersList: string[]) {
        if (!excludeVouchersList || !_.isArray(excludeVouchersList) || excludeVouchersList.length === 0) {
            return vouchers;
        }
        return vouchers.filter((voucher) => {
            const exVouchers = excludeVouchersList.filter((exVoucher) => {
                return parseInt(exVoucher) === parseInt(voucher.ID);
            });
            return !(exVouchers && exVouchers.length > 0);
        });
    }

    private _filterVouchers(cid: string, gameInfoList: GAMES_INFO): IAlteredFreePlayVoucher[] {
        const allVouchers = this.getAllVouchers().values();
        const filteredByDate: IFreePlayVoucherData[] = this._filterVouchersByDate(allVouchers);
        const availableVouchers = this._filterVouchersByAvailableGames(filteredByDate, cid, gameInfoList);
        return availableVouchers;
    }

    private _filterVouchersByDate(vouchers: IFreePlayVoucherData[]): IFreePlayVoucherData[] {
        const availableVouchers: IFreePlayVoucherData[] = [];
        vouchers.forEach((voucher: IFreePlayVoucherData) => {
            const timeToLeft = voucher.ExpirationDate.getTime() - new Date().getTime();
            const hasPendingDeletion: boolean = _.isNullOrUndefined(voucher.PendingDeletion) ? false : _.toBoolean(voucher.PendingDeletion);

            // add unexpired and not pending for deletion vouchers
            if (timeToLeft <= 0) {
                this.logger.info(`Voucher ${voucher.ID} is expired! Excluding from listing.`);
            } else if (hasPendingDeletion) {
                this.logger.info(`Voucher ${voucher.ID} is pending deletion! Excluding from listing.`);
            } else {
                availableVouchers.push(voucher);
            }
        });
        return availableVouchers;
    }

    /**
     * Filter vouchers and returns vouchers with the games exist in configuration.
     * @returns {Array}
     * @private
     */
    private _filterVouchersByAvailableGames(allVouchers: IFreePlayVoucherData[], cid: string, gameInfoList: GAMES_INFO): IAlteredFreePlayVoucher[] {
        const displayedIDs = this.getDisplayedFreePlayIDs(cid);

        const availableVouchers: IAlteredFreePlayVoucher[] = [];
        allVouchers.forEach((voucher: IFreePlayVoucherData) => {
            const voucherGames = voucher.GameTypeIDList;
            if (_.isNullOrUndefined(voucherGames)) {
                return;
            }

            let availableGames = this._filterVoucherGames(gameInfoList, voucherGames);
            availableGames = this._filterGamesByMinBet(gameInfoList, availableGames, voucher);
            if (availableGames.length > 0) {
                // give UI clone of original voucher, an altered voucher with additional properties
                const updatedVoucher: IAlteredFreePlayVoucher = Object.assign(
                    {},
                    voucher,
                    { games: availableGames },
                    { isNew: FreePlayService._isNewFreePlay(displayedIDs, voucher.ID) }
                );
                availableVouchers.push(updatedVoucher);
            } else {
                this.logger.debug(`All games in voucher ${voucher.ID} are not available. Voucher was skipped`);
            }
        });

        return availableVouchers;
    }


    /**
     * Filter voucher games according to gameList (gameList contains the games available in the site)
     * @param gameInfoList
     * @param voucherGames
     * @returns {Array}
     * @private
     */
    private _filterVoucherGames(gameInfoList: GAMES_INFO, voucherGames: number[]): number[] {
        if (!gameInfoList) {
            return [];
        }

        return voucherGames.filter((voucherGameID) => {
            return gameInfoList[voucherGameID];
        });
    }


    /**
     * Filter voucher games by minimum bet. Minimum bet of the game should be lower then voucher current balance
     * @param gameInfoList
     * @param voucherGames
     * @param voucher
     * @returns {Array}
     * @private
     */
    private _filterGamesByMinBet(gameInfoList: GAMES_INFO, voucherGames: number[], voucher: IFreePlayVoucherData): number[] {
        if (!gameInfoList || !voucherGames || !voucher) {
            return [];
        }

        return voucherGames.filter((voucherGame) => {
            const gameInfo = gameInfoList[voucherGame];
            // TODO uncomment once MInBet is available in GameInfo
            return gameInfo ?
                gameInfo.minBet === undefined || Number(gameInfo.minBet) <= Number(voucher.CurrentBalance) :
                false;
        });
    }


    /**
     * Clear on memory saved free plays or open the message
     * @private
     */
    private _onAuthChanged() {
        return new Promise((resolve, reject) => {
            if (!this.serviceFactory.oAuth2Service().isAuthorized()) {
                this._tripleEightVouchers.reset([]);
                reject();
            } else {
                if (this._shouldAutoOpenMessage) {
                    this._oAuthResolver = { resolve: resolve, reject: reject };
                }
            }
        });
    }


    /**
     * Return free play data for current user
     * @returns {*}
     */
    getFreePlayVouchers(): Promise<IFreePlayVoucherData[]> {
        return this._getFreePlayDataForUser();
    }


    /**
     * Returns true ig there is at least one FreePlay for current logged in user
     */
    areAnyFreePlays(): Promise<boolean> {
        return this._getFreePlayDataForUser().then((vouchers) => {
            return vouchers && vouchers.length > 0;
        });
    }

    getFreePlaysForGameType(gameType: number): Promise<IFreePlayVoucherData[]> {
        return this._getFreePlayDataForUser({ gameTypeID: gameType }).then(
            vouchers => (!vouchers || vouchers.length === 0) ? [] : vouchers
        );
    }

    /**
     * Fire event on any update of free play data for the current user
     * @private
     */
    private _notifyOnFreePlayChanges() {
        this._getFreePlayDataForUser().then((vouchers: IFreePlayVoucherData[]) => {
            this.logger.debug('Free play changes fire');
            Event.fire(SYSTEM_EVENTS.FREE_PLAY_UPDATED, vouchers);
        });
    }


    /**
     * Returns if the FreePlay ID is new (has not displayed yet)
     * @param displayedIDs
     * @param fpID
     * @returns {boolean}
     * @private
     */
    private static _isNewFreePlay(displayedIDs: string[], fpID: string) {
        let isNew = true;
        if (displayedIDs && displayedIDs.length > 0) {
            const foundID = displayedIDs.find((id) => {
                return id === fpID;
            });
            isNew = foundID ? false : true;
        }

        return isNew;
    }
}
